import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Phone, Mail, Users, Clock, CheckCircle2, XCircle, ChevronDown, ChevronUp, Trash2, Search, Send, UserPlus, Loader2, RefreshCw } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { useToast } from '../components/ui/Toast';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { DemoRequest, getDemoRequests, updateDemoRequestStatus, updateDemoRequestNotes, deleteDemoRequest } from '../utils/demoRequests';
const STATUS_CONFIG: Record<DemoRequest['status'], {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  nueva: {
    label: 'Nueva',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    icon: <Inbox size={14} data-id="element-2759" />
  },
  contactada: {
    label: 'Contactada',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    icon: <Phone size={14} data-id="element-2760" />
  },
  convertida: {
    label: 'Convertida',
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    icon: <CheckCircle2 size={14} data-id="element-2761" />
  },
  descartada: {
    label: 'Descartada',
    color: 'text-gray-500',
    bg: 'bg-gray-50 border-gray-200',
    icon: <XCircle size={14} data-id="element-2762" />
  }
};
export function DemoRequestsPage() {
  useDocumentTitle('Solicitudes de Demo');
  const {
    showToast
  } = useToast();
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todas');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');
  // Load requests from Supabase
  const loadRequests = useCallback(async () => {
    try {
      const data = await getDemoRequests();
      setRequests(data);
    } catch (e) {
      console.error('Error loading demo requests:', e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadRequests();
    // Listen for local events
    const handler = () => loadRequests();
    window.addEventListener('cricket-demo-request', handler);
    // Subscribe to Supabase real-time if configured
    let subscription: any = null;
    if (isSupabaseConfigured()) {
      try {
        subscription = supabaseClient.subscribeToDemoRequests(() => {
          loadRequests();
        });
      } catch (e) {
        console.warn('Could not subscribe to demo_requests:', e);
      }
    }
    return () => {
      window.removeEventListener('cricket-demo-request', handler);
      subscription?.unsubscribe?.();
    };
  }, [loadRequests]);
  // Filter
  const filtered = useMemo(() => {
    let result = requests;
    if (statusFilter !== 'Todas') {
      const statusKey = Object.entries(STATUS_CONFIG).find(([, v]) => v.label === statusFilter)?.[0];
      if (statusKey) result = result.filter(r => r.status === statusKey);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(lower) || r.company.toLowerCase().includes(lower) || r.email.toLowerCase().includes(lower));
    }
    return result;
  }, [requests, statusFilter, searchTerm]);
  // Stats
  const stats = useMemo(() => ({
    total: requests.length,
    nueva: requests.filter(r => r.status === 'nueva').length,
    contactada: requests.filter(r => r.status === 'contactada').length,
    convertida: requests.filter(r => r.status === 'convertida').length
  }), [requests]);
  const handleStatusChange = async (id: string, status: DemoRequest['status']) => {
    try {
      await updateDemoRequestStatus(id, status);
      await loadRequests();
      showToast('success', 'Estado actualizado', `Solicitud marcada como "${STATUS_CONFIG[status].label}"`);
    } catch (e) {
      showToast('error', 'Error', 'No se pudo actualizar el estado');
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await deleteDemoRequest(id);
      await loadRequests();
      showToast('success', 'Eliminada', 'Solicitud eliminada');
    } catch (e) {
      showToast('error', 'Error', 'No se pudo eliminar');
    }
  };
  const handleSaveNotes = async (id: string) => {
    try {
      await updateDemoRequestNotes(id, notesValue);
      await loadRequests();
      setEditingNotes(null);
      showToast('success', 'Notas guardadas', '');
    } catch (e) {
      showToast('error', 'Error', 'No se pudieron guardar las notas');
    }
  };
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return d.toLocaleDateString('es-DO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-[1400px] mx-auto flex items-center justify-center min-h-[60vh]" data-id="element-2763">
        <div className="text-center" data-id="element-2764">
          <Loader2 className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-3" data-id="element-2765" />
          <p className="text-gray-500 text-sm" data-id="element-2766">Cargando solicitudes...</p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-id="element-2767">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-2768">
        <div data-id="element-2769">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2770">
            Solicitudes de Demo
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2771">
            Leads del formulario de la landing page
            {isSupabaseConfigured() && <span className="ml-2 inline-flex items-center gap-1 text-green-600 text-xs font-medium" data-id="element-2772">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" data-id="element-2773" />
                Supabase
              </span>}
          </p>
        </div>
        <Button variant="ghost" size="sm" leftIcon={<RefreshCw size={14} data-id="element-2775" />} onClick={() => {
        setLoading(true);
        loadRequests();
      }} data-id="element-2774">
          Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-2776">
        <Card className="p-4 flex items-center gap-3" data-id="element-2777">
          <div className="p-2 bg-gray-100 rounded-lg" data-id="element-2778">
            <Inbox size={18} className="text-gray-600" data-id="element-2779" />
          </div>
          <div data-id="element-2780">
            <p className="text-[10px] text-gray-500 font-semibold uppercase" data-id="element-2781">
              Total
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-2782">{stats.total}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3 border-l-4 border-l-blue-500" data-id="element-2783">
          <div className="p-2 bg-blue-50 rounded-lg" data-id="element-2784">
            <UserPlus size={18} className="text-blue-600" data-id="element-2785" />
          </div>
          <div data-id="element-2786">
            <p className="text-[10px] text-gray-500 font-semibold uppercase" data-id="element-2787">
              Nuevas
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-2788">{stats.nueva}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3 border-l-4 border-l-amber-500" data-id="element-2789">
          <div className="p-2 bg-amber-50 rounded-lg" data-id="element-2790">
            <Phone size={18} className="text-amber-600" data-id="element-2791" />
          </div>
          <div data-id="element-2792">
            <p className="text-[10px] text-gray-500 font-semibold uppercase" data-id="element-2793">
              Contactadas
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-2794">
              {stats.contactada}
            </p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3 border-l-4 border-l-green-500" data-id="element-2795">
          <div className="p-2 bg-green-50 rounded-lg" data-id="element-2796">
            <CheckCircle2 size={18} className="text-green-600" data-id="element-2797" />
          </div>
          <div data-id="element-2798">
            <p className="text-[10px] text-gray-500 font-semibold uppercase" data-id="element-2799">
              Convertidas
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-2800">
              {stats.convertida}
            </p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-end" data-id="element-2801">
        <FilterDropdown label="Estado" value={statusFilter} options={['Todas', 'Nueva', 'Contactada', 'Convertida', 'Descartada']} onChange={setStatusFilter} className="w-full sm:w-44" data-id="element-2802" />
        <SearchBar placeholder="Buscar empresa o contacto..." onSearch={setSearchTerm} className="w-full sm:w-72" data-id="element-2803" />
      </div>

      {/* List */}
      {filtered.length === 0 ? <Card className="text-center py-16" noPadding={false} data-id="element-2804">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2805">
            <Inbox size={32} className="text-gray-300" data-id="element-2806" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2807">
            {requests.length === 0 ? 'Sin solicitudes aún' : 'No hay resultados'}
          </h3>
          <p className="text-gray-500 text-sm" data-id="element-2808">
            {requests.length === 0 ? 'Las solicitudes del formulario de demo aparecerán aquí.' : 'Intenta ajustar los filtros de búsqueda.'}
          </p>
        </Card> : <div className="space-y-3" data-id="element-2809">
          {filtered.map(req => {
        const isExpanded = expandedId === req.id;
        const cfg = STATUS_CONFIG[req.status];
        return <Card key={req.id} className="overflow-hidden" noPadding data-id="element-2810">
                {/* Main Row */}
                <div className={`p-4 lg:p-5 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50/50' : 'hover:bg-gray-50/50'}`} onClick={() => setExpandedId(isExpanded ? null : req.id)} data-id="element-2811">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6" data-id="element-2812">
                    <div className="flex items-center gap-3 min-w-0 lg:w-[280px]" data-id="element-2813">
                      <div className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.color}`} data-id="element-2814">
                        {cfg.icon}
                        {cfg.label}
                      </div>
                      <div className="min-w-0" data-id="element-2815">
                        <p className="font-bold text-gray-900 text-sm truncate" data-id="element-2816">
                          {req.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate" data-id="element-2817">
                          {req.company}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 flex-1 text-xs text-gray-500" data-id="element-2818">
                      <span className="flex items-center gap-1" data-id="element-2819">
                        <Mail size={12} data-id="element-2820" />
                        {req.email}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-2821">
                        <Phone size={12} data-id="element-2822" />
                        {req.phone}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-2823">
                        <Users size={12} data-id="element-2824" />
                        {req.employees}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 shrink-0" data-id="element-2825">
                      <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-2826">
                        <Clock size={12} data-id="element-2827" />
                        {formatDate(req.createdAt)}
                      </span>
                      {isExpanded ? <ChevronUp size={16} className="text-gray-400" data-id="element-2828" /> : <ChevronDown size={16} className="text-gray-400" data-id="element-2829" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && <div className="border-t border-gray-100 bg-gray-50/30 p-4 lg:p-5 space-y-4" data-id="element-2830">
                    {req.message && <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-2831">
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1" data-id="element-2832">
                          Mensaje
                        </p>
                        <p className="text-sm text-gray-700" data-id="element-2833">{req.message}</p>
                      </div>}

                    <div className="bg-white p-3 rounded-lg border border-gray-200" data-id="element-2834">
                      <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5" data-id="element-2835">
                        Notas internas
                      </p>
                      {editingNotes === req.id ? <div className="space-y-2" data-id="element-2836">
                          <textarea value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-none" placeholder="Agregar notas sobre este lead..." autoFocus data-id="element-2837" />
                          <div className="flex gap-2" data-id="element-2838">
                            <Button size="sm" onClick={() => handleSaveNotes(req.id)} data-id="element-2839">
                              Guardar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingNotes(null)} data-id="element-2840">
                              Cancelar
                            </Button>
                          </div>
                        </div> : <div onClick={e => {
                e.stopPropagation();
                setEditingNotes(req.id);
                setNotesValue(req.notes || '');
              }} className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 min-h-[32px] flex items-center" data-id="element-2841">
                          {req.notes || <span className="text-gray-400 italic" data-id="element-2842">
                              Click para agregar notas...
                            </span>}
                        </div>}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 pt-1" data-id="element-2843">
                      <p className="text-[10px] font-bold text-gray-400 uppercase mr-2" data-id="element-2844">
                        Cambiar estado:
                      </p>
                      {(Object.keys(STATUS_CONFIG) as DemoRequest['status'][]).map(status => <button key={status} onClick={e => {
                e.stopPropagation();
                handleStatusChange(req.id, status);
              }} disabled={req.status === status} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${req.status === status ? `${STATUS_CONFIG[status].bg} ${STATUS_CONFIG[status].color} opacity-60 cursor-default` : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer'}`} data-id="element-2845">
                          {STATUS_CONFIG[status].icon}
                          {STATUS_CONFIG[status].label}
                        </button>)}

                      <div className="flex-1" data-id="element-2846" />

                      <a href={`https://wa.me/${req.phone.replace(/[^\d+]/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors" data-id="element-2847">
                        <Send size={12} data-id="element-2848" />
                        WhatsApp
                      </a>

                      <button onClick={e => {
                e.stopPropagation();
                handleDelete(req.id);
              }} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 transition-colors" data-id="element-2849">
                        <Trash2 size={12} data-id="element-2850" />
                        Eliminar
                      </button>
                    </div>
                  </div>}
              </Card>;
      })}
        </div>}
    </div>;
}